interface ProcessSectionProps {
  onOpenConsultation: () => void;
}

const STEPS = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Free Consultation',
    desc: 'No obligation. We listen carefully to understand your loved one\'s specific needs, routines, and goals.',
    detail: '30 – 45 minutes',
    color: 'border-sage',
    hoverColor: 'hover:border-gold',
  },
  {
    number: '02',
    icon: 'ri-file-text-line',
    title: 'Personalized Care Plan',
    desc: 'Our coordinators build a fully customized plan covering daily routines, medical needs, and individual preferences.',
    detail: 'Ready in 24 hours',
    color: 'border-sage',
    hoverColor: 'hover:border-gold',
  },
  {
    number: '03',
    icon: 'ri-user-heart-line',
    title: 'Caregiver Matching',
    desc: 'We carefully select a caregiver matched by personality, skills, and the specific care requirements involved.',
    detail: 'Thoughtfully matched',
    color: 'border-sage',
    hoverColor: 'hover:border-gold',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Ongoing Monitoring',
    desc: 'Regular check-ins, care plan updates, and open family communication ensure consistent, evolving quality.',
    detail: 'Continuous support',
    color: 'border-sage',
    hoverColor: 'hover:border-gold',
  },
];

export default function ProcessSection({ onOpenConsultation }: ProcessSectionProps) {
  return (
    <section className="py-28 bg-white relative overflow-hidden">
      {/* Subtle dot background */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #0F2A44 1.5px, transparent 0)', backgroundSize: '40px 40px' }}
      />
      {/* Sage accent blob */}
      <div
        className="absolute top-0 right-0 w-96 h-96 opacity-[0.04] rounded-full pointer-events-none -translate-y-32 translate-x-32"
        style={{ background: '#8FAF9B' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-18" style={{ marginBottom: '4.5rem' }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Simple &amp; Clear</p>
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
          </div>
          <h2 className="font-heading text-navy font-bold mb-4 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            How Our Care Process Works
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            Getting started is simple. We guide every family through a clear, supportive process so care begins as quickly as possible.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-14 left-[13%] right-[13%] h-px border-t-2 border-dashed border-sage/35 z-0" />

          {STEPS.map((step, i) => (
            <div key={step.number} className="relative z-10 flex flex-col items-center text-center group">
              {/* Step circle */}
              <div className="relative mb-7">
                <div className={`w-28 h-28 rounded-full bg-offWhite border-2 ${step.color} ${step.hoverColor} transition-colors flex flex-col items-center justify-center gap-1.5 group-hover:bg-sage/10`}>
                  <span className="font-heading text-gold text-2xl font-bold leading-none">{step.number}</span>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${step.icon} text-navy text-lg`}></i>
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="lg:hidden absolute top-1/2 -right-4 w-8 h-px bg-sage/30" />
                )}
              </div>
              <span className="inline-flex items-center gap-1.5 bg-gold/12 text-gold text-[10px] font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-widest">
                <i className="ri-time-line text-xs"></i>
                {step.detail}
              </span>
              <h3 className="font-heading text-navy text-lg font-bold mb-2.5 leading-tight">{step.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA block */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-offWhite rounded-2xl p-9 flex flex-col md:flex-row items-center justify-between gap-6 border border-gray-100">
            <div>
              <h3 className="font-heading text-navy text-2xl font-bold mb-2 leading-tight">Ready to Start?</h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                Schedule your free consultation today — care can often begin within a few days.
              </p>
            </div>
            <button
              onClick={onOpenConsultation}
              className="whitespace-nowrap bg-navy text-white font-bold px-9 py-4 rounded-full hover:bg-[#1a3d5c] transition-colors cursor-pointer flex items-center gap-2.5 flex-shrink-0 group"
            >
              Start Your Care Plan
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>
          <a
            href="tel:2173462042"
            className="bg-gold rounded-2xl p-9 flex flex-col items-center justify-center text-center hover:brightness-105 transition-all group"
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-navy/12 mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-phone-fill text-navy text-2xl"></i>
            </div>
            <p className="font-heading text-navy text-xl font-bold">(217) 346-2042</p>
            <p className="text-navy/55 text-xs mt-1.5">Call Us · Mon–Sun 8am–6pm</p>
          </a>
        </div>
      </div>
    </section>
  );
}
